import Grid from 'Components/Atoms/Grid/Grid';
import Title from 'Components/Atoms/Title/Title';
import EditButton from 'Components/Molecules/EditButton/EditButton';
import FormGroup from 'Components/Molecules/Form/FormGroup/FormGroup';
import WarningModal from 'Components/Molecules/Modals/Actions/WarningModal/WarningModal';
import {default as OldField} from 'Pages/Dashboard/Settings/FieldsManagement/Fields/FieldTypes/Field';
import {useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {InternalDatasets} from 'Services/helpers';
import EntityHelper from 'Services/Settings/Entity/EntityHelper';
import DateHelper from 'Services/Utils/Date/DateHelper';
import {
    createBusinessUnitValues,
    fetchBusinessUnitV2, fetchBusinessUnitValues,
    updateBusinessUnit,
    updateBusinessUnitValues
} from 'Store/BusinessUnit/Actions';
import {
    createCandidateValues,
    fetchCandidateV2,
    fetchCandidateValues,
    updateCandidate,
    updateCandidateValues
} from 'Store/Candidate/Actions';
import {
    createContactValues,
    fetchContactV2,
    fetchContactValues,
    updateContact,
    updateContactValues
} from 'Store/Contact/Actions';
import {FieldType} from 'Store/FieldManagement/PropTypes';
import {StoreState} from 'Store/Reducers';
import {getEntityById} from 'Store/Settings/Entities/Selectors';
import {fetchEntityTypesByEntityId} from 'Store/Settings/EntityTypes/Actions';
import {getEntityTypeById} from 'Store/Settings/EntityTypes/Selector';
import {getFieldsByEntityIdAsArray, getFieldsByEntityIdAsObject, getFieldsById} from 'Store/Settings/Fields/Selectors';
import {getUsedFieldsInLayoutsByEntityId} from 'Store/Settings/Layout/Selectors';
import {
    getRecordById,
    getValuesByEntityNameByEntityId,
    isEntityCreatingByEntityName,
    isEntityPatching
} from 'Store/Shared/Store/Values/Selector';
import {
    createVacancyValues,
    fetchVacancyV2,
    fetchVacancyValues,
    updateVacancy,
    updateVacancyValues
} from 'Store/Vacancy/Actions';
import Field from '../Field/Field';
import styles from './LayoutSystem.module.sass';
import {useTab} from 'Services/Tab/TabContext';

const LayoutSystem = ({
    entity_id, record_id,
    entity, record,
    values,
    fieldById,
    fields, fieldsAsArray, fieldsUsed, entityType,

    isEntityPatching, isEntityCreating,

    ...props
}: LayoutSystemProps) => {
    const fetchRecord = useMemo(() => EntityHelper.fetchRecordById(props, entity?.name), [entity?.name]);
    const createValues = useMemo(() => EntityHelper.createValue(props, entity?.name), [entity?.name]);
    const updateValues = useMemo(() => EntityHelper.updateValue(props, entity?.name), [entity?.name]);
    const updateRecord = useMemo(() => EntityHelper.update(props, entity?.name), [entity?.name]);
    const sidebarFieldIds = useMemo(() => entityType?.sidebar_field_ids ?? [], [entityType]);
    
    const [state, setState] = useState<any>({
        isEdit: false,
        isValid: false,
        changedPublicFields: []
    });

    const [data, setData] = useState({});

    const handleEdit = (value: any) => {
        if (!value) setData({});

        const isValid = sidebarFieldIds
            .map(i => fieldById?.[i])
            .filter(i => i.required)
            .every(field => {
                let value = values?.[field.id]?.value;
                if (!value) return false;
                if (field?.type === FieldType.Combination) {
                    let isValid = false;
                    value.forEach(i => {
                        if (isValid) return;
        
                        isValid = field?.default?.fields.every(j => i?.[j]?.length > 0);
                    });
        
                    return isValid;
                }
        
                if (typeof value == 'number') {
                    return value > 0;
                }
        
                return value?.length > 0;
            });

        setState((state) => ({
            ...state,
            isEdit: value,
            isValid,
            changedPublicFields: []
        }));
    };

    const handleSaveValidation = () => {
        if (record?.is_published || record?.is_sponsored) {
            const publicFields = sidebarFieldIds
                ?.map(fieldId => fieldById?.[fieldId])
                ?.filter(i => i.is_public);

            const fields = Object.keys(data);
            const changedPublicFields = fields
                ?.map(fieldId => fieldById?.[fieldId])
                ?.filter(i => publicFields.includes(i));
            
            setState((state) => ({
                ...state,
                changedPublicFields
            }));
            
            if (changedPublicFields.length === 0) {
                handleSave();
            }

            return;
        }

        handleSave();
    }

    const handleSave = () => {
        let createValuesPayload = {};
        let updateValuesPayload = {};

        const fieldIds = Object.keys(data)?.filter(i => sidebarFieldIds?.includes(i));
        for (const fieldId of fieldIds) {
            const item = data?.[fieldId];
            if (!item?.value_id) {
                createValuesPayload[fieldId] = item?.value;
                
                continue;
            }

            updateValuesPayload[item.value_id] = item.value;
        }
        
        const hasCreateValues = Object.keys(createValuesPayload).length > 0;
        const hasUpdateValues = Object.keys(updateValuesPayload).length > 0;
        
        if (hasCreateValues) createValues(record_id, createValuesPayload);
        if (hasUpdateValues) updateValues(record_id, updateValuesPayload);

        if (data?.['owners']) {
            updateRecord(record_id, {
                owners: data?.['owners']?.value ?? record?.owners
            });
        }

        if (!hasCreateValues
            && !hasUpdateValues
            && !data?.['owners']) {
            setState((state) => ({
                ...state,
                isEdit: false
            }));
        }
    }

    const handleChange = (fieldId: string) => (value: any) => {
        const newValues = {
            ...data,
            [fieldId]: {
                ...values?.[fieldId],
                value: value
            }
        };

        const isValid = sidebarFieldIds
            .map(i => fieldById?.[i])
            .filter(i => i.required)
            .every(field => {
                let value = ({
                    ...values,
                    ...newValues
                })?.[field.id]?.value;
                if (!value) return false;
                if (field?.type === FieldType.Combination) {
                    let isValid = false;
                    value.forEach(i => {
                        if (isValid) return;
        
                        isValid = field?.default?.fields.every(j => i?.[j]?.length > 0);
                    });
        
                    return isValid;
                }

                if (field?.type === FieldType.Address) {
                    return !!value?.address?.locality;
                }
        
                if (typeof value == 'number') {
                    return value > 0;
                }
        
                return value?.length > 0;
            });

        console.log(isValid);

        setData(newValues);
        setState((state) => ({
            ...state,
            isValid
        }));
    }

    const { state: tabState } = useTab();

    useEffect(() => {
        if (tabState?.previousTab) fetchRecord(record_id);
    }, []);

    return <div className={styles['layoutsystem']}>
        {
            state.changedPublicFields.length > 0 &&
            <WarningModal
                onCancel={handleEdit}
                onConfirm={handleSave}
            >
                De volgende velden worden aangepast en staan live op de website. Deze aanpassingen worden direct doorgevoerd naar de website. Weet je zeker dat je deze wilt aanpassen?
                <ul>
                    { state.changedPublicFields.map(field => <li>{field?.name}</li>) }
                </ul>
            </WarningModal>
        }

        <div className={styles['layoutsystem__content']}>
            <div className={styles['layoutsystem__content-header']}>
                <Grid
                    position='between'
                    align='center'
                    axis='horizontal'
                >
                    <Title weight='semibold'>
                        Algemeen
                    </Title>
                    <EditButton
                        variant='button'
                        isEdit={state.isEdit}
                        onChange={handleEdit}
                        onSave={handleSaveValidation}
                        isLoading={isEntityPatching || isEntityCreating}
                        isDisabled={state.isEdit && !state.isValid}
                    />
                </Grid>
            </div>

            {
                sidebarFieldIds?.map((fieldId: any) => {
                    const field = fieldById?.[fieldId];

                    if (!field?.name) {
                        return <FormGroup title='Veld niet gevonden...'>
                            Oops..
                        </FormGroup>
                    }

                    return <FormGroup
                        title={field?.name}
                        isPublic={field?.is_public}
                        isRequired={state.isEdit && field?.required}
                        isTitle={entity?.name_fields?.includes(field.id)}
                    >
                        <OldField
                            isEdit={state.isEdit}
                            type={field.type}
                            value={(data?.[fieldId] ?? values?.[fieldId])?.value}
                            onChange={handleChange(fieldId)}
                            fieldProperties={{
                                ...field?.default,
                                alignLeft: true
                            }}
                        />
                    </FormGroup>
                })
            }

            {
                typeof record?.['owners'] !== 'undefined' &&
                <FormGroup title='Intern verantwoordelijke'>
                    <Field
                        isEdit={state.isEdit}
                        value={data?.['owners']?.value ?? record?.['owners']}
                        type={FieldType.Multi_Selector}
                        onChange={handleChange('owners')}
                        properties={{
                            dataset: [InternalDatasets.USERS],
                            alignLeft: true
                        }}
                    />
                </FormGroup>
            }
        </div>
        <div className={styles['layoutsystem__creation']}>
            <div>
                Laatst geupdate op <span>{ DateHelper.getFullDate(record?.updated_at.date) }</span> door <span>{ record?.updated_by }</span><br/>
            </div>
            <div>
                Aangemaakt op <span>{ DateHelper.getFullDate(record?.created_at.date) }</span> door <span>{ record?.created_by }</span>
            </div>
        </div>
    </div>;
}

interface Props {
    entity_id: any
    record_id: any
}

const mapState = (state: StoreState, props: Props) => ({
    entity: getEntityById(state, props.entity_id),
    record: getRecordById(state, props.record_id),

    fieldById: getFieldsById(state),

    fields: getFieldsByEntityIdAsObject(state, props.entity_id),
    fieldsAsArray: getFieldsByEntityIdAsArray(state, props.entity_id),
    fieldsUsed: getUsedFieldsInLayoutsByEntityId(state, props.entity_id),

    values: getValuesByEntityNameByEntityId(state, getEntityById(state, props.entity_id)?.name, props.record_id),
    entityType: getEntityTypeById(state, getRecordById(state, props.record_id)?.type_id),

    isEntityPatching: isEntityPatching(state, getEntityById(state, props.entity_id)?.name),
    isEntityCreating: isEntityCreatingByEntityName(state, getEntityById(state, props.entity_id)?.name)
});

const mapDispatch = {
    fetchEntityTypesByEntityId,

    updateVacancy,
    updateCandidate,
    updateBusinessUnit,
    updateContact,

    updateVacancyValues,
    updateCandidateValues,
    updateBusinessUnitValues,
    updateContactValues,

    createVacancyValues,
    createCandidateValues,
    createBusinessUnitValues,
    createContactValues,

    fetchContactV2,
    fetchContactValues,
    fetchBusinessUnitV2,
    fetchBusinessUnitValues,
    fetchCandidateV2,
    fetchCandidateValues,
    fetchVacancyV2,
    fetchVacancyValues
}

type LayoutSystemProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(LayoutSystem);